import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspaceResolver } from './useWorkspaceResolver';
import { useAuth } from '@/lib/auth';

export type ActionType = 'whatsapp' | 'call' | 'done' | 'defer' | 'note' | 'email';
export type ActionOutcome = 'contacted' | 'no_answer' | 'booked_call' | 'not_interested' | 'closed_won' | 'closed_lost';

export function useLogAction() {
  const queryClient = useQueryClient();
  const { workspaceId } = useWorkspaceResolver();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      leadId,
      actionType,
      outcome,
    }: {
      leadId: string;
      actionType: ActionType;
      outcome?: ActionOutcome;
    }) => {
      if (!workspaceId || !user?.id) throw new Error('No workspace or user');

      // Check if action_events table exists
      const testQuery = await supabase
        .from('action_events')
        .select('id')
        .limit(1);
      
      if (testQuery.error && (testQuery.error.code === '42P01' || testQuery.error.message?.includes('does not exist'))) {
        // Table doesn't exist, silently succeed (don't break the app)
        console.warn('[useLogAction] action_events table does not exist, skipping log');
        return null;
      }

      // Use type assertion for strict Supabase types
      const { data, error } = await supabase
        .from('action_events')
        .insert({
          workspace_id: workspaceId,
          user_id: user.id,
          lead_id: leadId,
          action_type: actionType,
          outcome: outcome || null,
        } as any)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: ['action-events', workspaceId] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-stats', workspaceId] });
      }
    },
  });
}

export function useActionEvents(days: number = 7) {
  const { workspaceId } = useWorkspaceResolver();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  return useQuery({
    queryKey: ['action-events', workspaceId, days],
    queryFn: async () => {
      if (!workspaceId) return [];

      // Check if action_events table exists
      const testQuery = await supabase
        .from('action_events')
        .select('id')
        .limit(1);
      
      if (testQuery.error && (testQuery.error.code === '42P01' || testQuery.error.message?.includes('does not exist'))) {
        // Table doesn't exist, return empty array
        return [];
      }

      const { data, error } = await supabase
        .from('action_events')
        .select('*')
        .eq('workspace_id', workspaceId as any)
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) {
        // Handle 400 errors gracefully (invalid API key/config)
        if (error.code === '400' || error.message?.includes('400') || error.message?.includes('API key')) {
          console.error('[useActionEvents] Supabase 400 error:', {
            code: error.code,
            message: error.message,
            hint: 'Check Vercel environment variables: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY'
          });
          // Return empty array instead of throwing to prevent crash
          return [];
        }
        // Handle table not found
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          return [];
        }
        throw error;
      }
      return data || [];
    },
    enabled: !!workspaceId,
  });
}

export function useDashboardStats() {
  const { workspaceId } = useWorkspaceResolver();

  return useQuery({
    queryKey: ['dashboard-stats', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return null;

      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const sevenDaysAgo = new Date(todayStart);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const thirtyDaysAgo = new Date(todayStart);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // Check if action_events table exists by trying a simple query
      // If it doesn't exist, return safe defaults
      let actionEventsTableExists = true;
      try {
        const testQuery = await supabase
          .from('action_events')
          .select('id')
          .limit(1);
        
        if (testQuery.error && (testQuery.error.code === '42P01' || testQuery.error.message?.includes('does not exist'))) {
          actionEventsTableExists = false;
        }
      } catch (err) {
        actionEventsTableExists = false;
      }

      // Fetch all required data in parallel with error handling
      const [
        actionsToday,
        actionsLast7Days,
        actionsLast30Days,
        bookedCallsLast7Days,
        leads,
      ] = await Promise.all([
        // Actions today (only if table exists)
        actionEventsTableExists
          ? supabase
              .from('action_events')
              .select('id', { count: 'exact' })
              .eq('workspace_id', workspaceId as any)
              .gte('created_at', todayStart.toISOString())
          : Promise.resolve({ data: null, error: null, count: 0 }),
        
        // Actions last 7 days (only if table exists)
        actionEventsTableExists
          ? supabase
              .from('action_events')
              .select('id, created_at, action_type, outcome')
              .eq('workspace_id', workspaceId as any)
              .gte('created_at', sevenDaysAgo.toISOString())
          : Promise.resolve({ data: [], error: null }),
        
        // Actions last 30 days (only if table exists)
        actionEventsTableExists
          ? supabase
              .from('action_events')
              .select('id, created_at, action_type, outcome')
              .eq('workspace_id', workspaceId as any)
              .gte('created_at', thirtyDaysAgo.toISOString())
          : Promise.resolve({ data: [], error: null }),
        
        // Booked calls last 7 days (only if table exists)
        actionEventsTableExists
          ? supabase
              .from('action_events')
              .select('id', { count: 'exact' })
              .eq('workspace_id', workspaceId as any)
              .eq('outcome', 'booked_call' as any)
              .gte('created_at', sevenDaysAgo.toISOString())
          : Promise.resolve({ data: null, error: null, count: 0 }),
        
        // Leads for temperature distribution and overdue
        supabase
          .from('leads')
          .select('id, temperature, pipeline_stage, next_action_at')
          .eq('workspace_id', workspaceId as any)
          .not('pipeline_stage', 'in', '(won,lost)'),
      ]);

      // Handle 400 errors gracefully (invalid API key/config) - check all results
      const results = [actionsToday, actionsLast7Days, actionsLast30Days, bookedCallsLast7Days, leads];
      for (const result of results) {
        if (result.error) {
          if (result.error.code === '400' || result.error.message?.includes('400') || result.error.message?.includes('API key')) {
            console.error('[useDashboardStats] Supabase 400 error:', {
              code: result.error.code,
              message: result.error.message,
              hint: 'Check Vercel environment variables: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY'
            });
            // Return safe defaults instead of throwing to prevent crash
            return {
              actionsToday: 0,
              actionsLast7Days: 0,
              bookedCallsLast7Days: 0,
              overdueFollowups: 0,
              hotLeadsCount: 0,
              temperatureDistribution: { hot: 0, warm: 0, cold: 0 },
              actionsDaily7: {},
              actionsDaily30: {},
              totalActiveLeads: 0,
            };
          }
        }
      }

      // Cast leads data to proper type
      const leadsData = (leads.data || []) as Array<{ id: string; temperature: string; pipeline_stage: string; next_action_at: string | null }>;

      // Calculate overdue followups
      const overdueLeads = leadsData.filter(lead => {
        if (!lead.next_action_at) return false;
        return new Date(lead.next_action_at) < now;
      });

      // Calculate hot leads
      const hotLeads = leadsData.filter(lead => lead.temperature === 'hot');

      // Calculate temperature distribution
      const temperatureDistribution = {
        hot: leadsData.filter(l => l.temperature === 'hot').length,
        warm: leadsData.filter(l => l.temperature === 'warm').length,
        cold: leadsData.filter(l => l.temperature === 'cold').length,
      };

      // Group actions by day for charts - cast to proper type
      const actionsData7 = (actionsLast7Days.data || []) as Array<{ created_at: string }>;
      const actionsData30 = (actionsLast30Days.data || []) as Array<{ created_at: string }>;
      
      const groupByDay = (events: Array<{ created_at: string }>) => {
        const grouped: Record<string, number> = {};
        events.forEach(event => {
          const date = new Date(event.created_at).toISOString().split('T')[0];
          grouped[date] = (grouped[date] || 0) + 1;
        });
        return grouped;
      };

      const actionsDaily7 = groupByDay(actionsData7);
      const actionsDaily30 = groupByDay(actionsData30);

      return {
        actionsToday: actionsToday.count || 0,
        actionsLast7Days: actionsData7.length,
        bookedCallsLast7Days: bookedCallsLast7Days.count || 0,
        overdueFollowups: overdueLeads.length,
        hotLeadsCount: hotLeads.length,
        temperatureDistribution,
        actionsDaily7,
        actionsDaily30,
        totalActiveLeads: leadsData.length,
      };
    },
    enabled: !!workspaceId,
    refetchInterval: 60000, // Refresh every minute
  });
}
